import React from 'react';
import { find, min, max } from 'lodash';
import style from './index.module.css';
import { createConnector } from 'react-instantsearch-dom';
import { RangeSlider as RangeSliderComponent } from '../../../atoms/range_slider';

let rangeMin: number | undefined;
let rangeMax: number | undefined;

export type Facet = { name: string; stats: { max: number; min: number } };

export const getMaxMinPriceFromFacets = (facets: Array<Facet>) => {
    const lowEstimateInfo = find(facets, (facet) => facet.name === 'lowEstimate');
    const highEstimateInfo = find(facets, (facet) => facet.name === 'highEstimate');
    return {
        min: min([lowEstimateInfo?.stats.min, highEstimateInfo?.stats.min]),
        max: max([lowEstimateInfo?.stats.max, highEstimateInfo?.stats.max]),
    };
};

const connectPriceFilter = createConnector({
    displayName: 'PriceFilterWidget',
    getProvidedProps(props, searchState, searchResults) {
        if (isNaN(rangeMin) || isNaN(rangeMax)) {
            const { max, min } = getMaxMinPriceFromFacets(
                searchResults.results && searchResults.results.disjunctiveFacets,
            );

            rangeMin = min;
            rangeMax = max;
        }

        const currentRefinement = {
            sliderPricePosLow: searchState.sliderPricePosLow || rangeMin,
            sliderPricePosHigh: searchState.sliderPricePosHigh || rangeMax,
            min: rangeMin,
            max: rangeMax,
        };

        return { currentRefinement };
    },
    refine(props, searchState, nextRefinement) {
        return {
            ...searchState,
            sliderPricePosLow: nextRefinement.sliderPricePosLow,
            sliderPricePosHigh: nextRefinement.sliderPricePosHigh,
        };
    },
    getSearchParameters(searchParameters, props, searchState) {
        const search = searchParameters.addDisjunctiveFacet('lowEstimate').addDisjunctiveFacet('highEstimate');
        if (searchState.sliderPricePosHigh && searchState.sliderPricePosLow) {
            return search
                .addNumericRefinement('lowEstimate', '<=', searchState.sliderPricePosHigh)
                .addNumericRefinement('highEstimate', '>=', searchState.sliderPricePosLow);
        }
        return search;
    },
    cleanUp(_props, searchState) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { sliderPricePosLow, sliderPricePosHigh, ...nextSearchState } = searchState;
        return nextSearchState;
    },
});

type CustomPriceFilterProps = {
    currentRefinement: {
        min: number;
        max: number;
        sliderPricePosLow: number;
        sliderPricePosHigh: number;
    };
    refine(value?: { sliderPricePosLow: number; sliderPricePosHigh: number }): void;
};

const CustomPriceFilter = ({ currentRefinement, refine }: CustomPriceFilterProps) => {
    return (
        <RangeSliderComponent
            onChange={({ values: [newMin, newMax] }) => {
                refine({
                    sliderPricePosLow: newMin,
                    sliderPricePosHigh: newMax,
                });
            }}
            min={currentRefinement.min}
            max={currentRefinement.max}
            sliderPricePosLow={currentRefinement.sliderPricePosLow}
            sliderPricePosHigh={currentRefinement.sliderPricePosHigh}
        />
    );
};

const ConnectedPriceFilter = connectPriceFilter(CustomPriceFilter);

type Props = {
    label: string;
};

const WrappedFilter = ({ label }: Props) => {
    return (
        <div className={style.filterWrapper}>
            <div className={style.accordionHeader}>{label}</div>
            <ConnectedPriceFilter />
        </div>
    );
};

export default WrappedFilter;
